/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { generatePath, Link } from "react-router-dom";
import styled from "styled-components";
import { Tooltip, theme, Text } from "@nordcloud/gnui";
import {
  WasteIndicator,
  Co2eSavings,
  COST_DEFAULT_SUGGESTION,
  ConditionalWrap,
} from "~/components";
import { ROUTES } from "~/routing/routes";
import { isNotEmpty } from "~/tools";

type WrapperProps = {
  inline: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  position: relative;
  display: ${({ inline }) => (inline ? "inline-flex" : "flex")};
  align-items: center;
  justify-content: flex-end;

  & > div {
    display: flex;
    margin-top: -2px;
  }

  & > span {
    margin-left: ${theme.spacing.spacing04};
  }
`;

type Props = {
  estateId: string;
  tooltip?: boolean;
  value?: string;
  inline?: boolean;
  isWaste?: boolean;
  reason?: string;
  count: number;
  amount: string;
  co2e?: number;
  co2eSavings?: number;
  extraInfo?: string;
  costTooltipValue?: string;
};

export function EstateCostCell({
  estateId,
  value = "0",
  inline = false,
  isWaste = false,
  reason,
  amount = "0",
  count = 0,
  co2e = 0,
  co2eSavings = 0,
  extraInfo,
  costTooltipValue,
}: Props) {
  const wasteReason = isWaste ? reason : COST_DEFAULT_SUGGESTION;
  const showWasteIndicator = parseFloat(amount) > 0 && isWaste;
  const tooltipReason =
    count > 1 ? `${count} savings suggestions found` : wasteReason;
  const showCo2eSavings = co2e > 0;

  const showCostTooltip = isNotEmpty(costTooltipValue ?? "");
  return (
    <>
      <Wrapper inline={inline}>
        <ConditionalWrap
          condition={showCostTooltip}
          wrap={(children) => (
            <Tooltip
              caption={costTooltipValue}
              hideTimeout={0}
              minWidth="16rem"
            >
              {children}
            </Tooltip>
          )}
        >
          {CostSpan(value, showCostTooltip)}
        </ConditionalWrap>
      </Wrapper>
      <Wrapper inline={inline}>
        <When condition={showWasteIndicator}>
          <When condition={showCo2eSavings}>
            <Co2eSavings iconOnly co2eSavings={co2eSavings} />
          </When>
          <Link to={`/estate/${estateId}`}>
            <WasteIndicator
              reason={tooltipReason}
              size="sm"
              amount={{
                amount,
                uri: undefined,
              }}
            />
          </Link>
        </When>
        <When condition={extraInfo}>
          <Link
            to={generatePath(ROUTES.resource, {
              nordcloudId: estateId,
            })}
          >
            <Text
              size="sm"
              tag="span"
              color={theme.color.text.text02}
              css={{
                textAlign: "end",
                marginLeft: theme.spacing.spacing02,
                marginTop: theme.spacing.spacing00,
              }}
            >
              {extraInfo}
            </Text>
          </Link>
        </When>
      </Wrapper>
    </>
  );
}

function CostSpan(value: string, hasTooltip: boolean) {
  return (
    <span
      css={{
        color: hasTooltip ? theme.color.text.text02 : undefined,
      }}
    >
      {value}
    </span>
  );
}
